/**
 * Payment Settings Form Components
 *
 * Three concerns, separated:
 *   1. PaymentMethodCard — selects which method to CONFIGURE (local UI state only)
 *   2. Config sections — CollectionSetupCard, PaymentTermsCard, LateFeeConfig
 *   3. PaymentActivationCard — the "go live" toggle (sets paymentMethod)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { type PaymentConfig, type UpdatePaymentConfigInput, type LandlordFundingSource, type PropertyBankInfo, type PaymentReadiness } from '../../lib/api';
import type { LinkedBankAccount } from '../../api/linked-bank-accounts.api';
import type { LeaseState } from '../../types/lease';
import type { MethodSelection } from './payment-helpers';
import { HelpBlock, HelpTooltip } from '../ui/HelpTooltip';
import { ACH_PAYMENT_FEE_CENTS } from '@resida/api-types/plan-copy';
import { formatUsd } from '../../api/billing';

/** The per-ACH-payment fee as shown to landlords, formatted from the one shared constant. */
const ACH_FEE = formatUsd(ACH_PAYMENT_FEE_CENTS);

/* ── Card 1: Payment Method Selector (local state only — no server flags) ── */

interface PaymentMethodCardProps {
  selectedMethod: MethodSelection;
  onMethodChange: (method: MethodSelection) => void;
}

export function PaymentMethodCard({ selectedMethod, onMethodChange }: PaymentMethodCardProps) {
  const optionClass = (active: boolean) =>
    `flex-1 min-w-[140px] px-3 py-3 text-sm rounded-lg border-2 transition-colors text-left cursor-pointer ${
      active
        ? 'border-primary bg-primary/5 text-primary'
        : 'border-border bg-background text-foreground hover:border-muted-foreground/30 hover:bg-accent'
    }`;

  const options: Array<{
    key: MethodSelection;
    testId: string;
    label: string;
    description: string;
  }> = [
    { key: 'ach', testId: 'payment-method-ach', label: 'ACH / Bank Transfer', description: 'Tenant pays through the platform via bank transfer' },
    { key: 'external', testId: 'payment-method-external', label: 'External (Zelle / Cash)', description: 'Track payments made via Zelle, cash, or check' },
    { key: 'none', testId: 'payment-method-none', label: 'Not Configured', description: 'No payment method selected' },
  ];

  return (
    <div data-testid="payment-method-card" className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold m-0">Payment Method</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-3">
        Select a payment method to configure
      </p>
      <div className="mb-5">
        <HelpBlock
          summary="Each lease uses one payment method. ACH collects rent through the platform via bank transfer. External detects payments you receive outside the platform (Zelle, cash, checks)."
          details={`ACH / Bank Transfer: Tenants pay directly through the platform. Payments are processed via bank transfer (3\u20135 business days to settle). Late fees are assessed automatically. Cost: a ${ACH_FEE} Stripe fee per transaction, deducted from the deposit to your bank account.\n\nExternal (Zelle / Cash): You receive payments through your own channels. The system monitors your linked bank account and matches incoming deposits to tenants. Late fees require your approval since the system can't be 100% certain about external payments.\n\nYou can switch methods at any time, but only one can be active per lease.`}
        />
      </div>

      <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Payment method">
        {options.map(opt => (
          <button
            key={opt.key}
            type="button"
            data-testid={opt.testId}
            role="radio"
            aria-checked={selectedMethod === opt.key}
            onClick={() => onMethodChange(opt.key)}
            className={optionClass(selectedMethod === opt.key)}
          >
            <div className="font-semibold text-sm mb-0.5">{opt.label}</div>
            <div className="text-xs text-muted-foreground">
              {opt.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Payment Activation Card (the "go live" toggle) ── */

interface PaymentActivationCardProps {
  paymentConfig: PaymentConfig | null;
  selectedMethod: MethodSelection;
  readiness: PaymentReadiness;
  onUpdate: (data: UpdatePaymentConfigInput) => void;
  /** BR-LL-10/BR-LL-11 — `paymentMethod` stays configured after tenancy ends/archives
   *  (it's a historical record, not a live/dead flag), so this card must not infer
   *  "active" from `paymentMethod !== 'none'` alone once the lease is past `active`. */
  leaseState?: LeaseState;
}

export function PaymentActivationCard({ paymentConfig, selectedMethod, readiness, onUpdate, leaseState }: PaymentActivationCardProps) {
  const currentMethod = paymentConfig?.paymentMethod ?? 'none';
  const isLive = currentMethod !== 'none';

  // Can the selected method be activated?
  const wantsAch = selectedMethod === 'ach';
  const wantsExternal = selectedMethod === 'external';
  const achReady = readiness.ach.ready;
  const externalReady = readiness.external.ready;

  const canActivate =
    selectedMethod !== 'none' &&
    (!wantsAch || achReady) &&
    (!wantsExternal || externalReady);

  // Collect all blocking issues for the selected method
  const blockingIssues: string[] = [];
  if (wantsAch && !achReady) {
    blockingIssues.push(...readiness.ach.issues.filter(i => i.severity === 'fatal').map(i => i.message));
  }
  if (wantsExternal && !externalReady) {
    blockingIssues.push(...readiness.external.issues.filter(i => i.severity === 'fatal').map(i => i.message));
  }

  const handleActivate = () => {
    onUpdate({ paymentMethod: selectedMethod as 'ach' | 'external' });
  };

  const handleDeactivate = () => {
    onUpdate({ paymentMethod: 'none' });
  };

  if (isLive && leaseState === 'archived') {
    return (
      <div data-testid="payment-activation-card" className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold m-0">Payment Status</h3>
        <div className="mt-3 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Rent collection stopped — lease archived</span>
        </div>
        <p className="text-sm text-muted-foreground m-0 mt-2">
          The lease is archived and read-only — the payment method cannot be changed or deactivated.
        </p>
      </div>
    );
  }

  if (isLive && leaseState === 'ended') {
    return (
      <div data-testid="payment-activation-card" className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold m-0">Payment Status</h3>
        <div className="mt-3 flex items-center gap-2 mb-3">
          <div className="h-2 w-2 rounded-full bg-warning" />
          <span className="text-sm font-medium text-warning">Automatic collection stopped</span>
        </div>
        <p className="text-sm text-muted-foreground m-0 mb-4">
          Tenancy has ended — no new charges or reminders are generated. Settlement continues until the lease is archived.
        </p>
        <button
          type="button"
          data-testid="deactivate-payments-btn"
          onClick={handleDeactivate}
          className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/5"
        >
          Deactivate Payments
        </button>
      </div>
    );
  }

  // Method selection matches what's currently enabled
  const selectionMatchesEnabled = selectedMethod === currentMethod;

  if (selectedMethod === 'none') {
    if (isLive) {
      // Currently live but user selected "Not Configured" — offer to deactivate
      return (
        <div data-testid="payment-activation-card" className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold m-0">Payment Status</h3>
              <p className="text-sm text-muted-foreground mt-1 m-0">
                Payments are currently <span className="font-medium text-success">active</span>
              </p>
            </div>
            <button
              type="button"
              data-testid="deactivate-payments-btn"
              onClick={handleDeactivate}
              className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/5"
            >
              Deactivate Payments
            </button>
          </div>
        </div>
      );
    }
    return null; // No method selected, not live — nothing to show
  }

  return (
    <div data-testid="payment-activation-card" className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold m-0">Payment Status</h3>

      {isLive && selectionMatchesEnabled ? (
        // Currently live with the selected method
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-success" />
            <span className="text-sm font-medium text-success">Payments Active</span>
          </div>
          <p className="text-sm text-muted-foreground m-0 mb-4">
            Charges are being generated and payments are being collected.
          </p>
          <button
            type="button"
            data-testid="deactivate-payments-btn"
            onClick={handleDeactivate}
            className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/5"
          >
            Deactivate Payments
          </button>
        </div>
      ) : isLive && !selectionMatchesEnabled ? (
        // Currently live but user changed method selection — offer to switch
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-success" />
            <span className="text-sm font-medium text-success">Payments Active</span>
            <span className="text-xs text-muted-foreground">(different method)</span>
          </div>
          {canActivate ? (
            <>
              <p className="text-sm text-muted-foreground m-0 mb-4">
                Switch payment method and reactivate.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="activate-payments-btn"
                  onClick={handleActivate}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
                >
                  Switch & Activate
                </button>
                <button
                  type="button"
                  data-testid="deactivate-payments-btn"
                  onClick={handleDeactivate}
                  className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/5"
                >
                  Deactivate
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 p-3 rounded-md border border-warning/30 bg-warning/10 text-sm" data-testid="activation-issues">
                <p className="font-medium text-warning m-0 mb-1">Setup required before activating:</p>
                <ul className="list-disc ml-4 text-warning m-0">
                  {blockingIssues.map((msg, i) => <li key={i}>{msg}</li>)}
                </ul>
              </div>
              <button
                type="button"
                data-testid="deactivate-payments-btn"
                onClick={handleDeactivate}
                className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/5"
              >
                Deactivate
              </button>
            </>
          )}
        </div>
      ) : canActivate ? (
        // Not live, ready to activate
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Payments Inactive</span>
          </div>
          <p className="text-sm text-muted-foreground m-0 mb-4">
            Configuration complete. Activate to begin collecting payments.
          </p>
          <button
            type="button"
            data-testid="activate-payments-btn"
            onClick={handleActivate}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
          >
            Activate Payments
          </button>
        </div>
      ) : (
        // Not live, not ready
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Payments Inactive</span>
          </div>
          <div className="p-3 rounded-md border border-warning/30 bg-warning/10 text-sm" data-testid="activation-issues">
            <p className="font-medium text-warning m-0 mb-1">Complete the following to activate payments:</p>
            <ul className="list-disc ml-4 text-warning m-0">
              {blockingIssues.map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Card 2: Payment Terms ── */

interface PaymentTermsCardProps {
  paymentConfig: PaymentConfig | null;
  onUpdate: (data: UpdatePaymentConfigInput) => Promise<boolean>;
}

export function PaymentTermsCard({ paymentConfig, onUpdate }: PaymentTermsCardProps) {
  const [rentDueDay, setRentDueDay] = useState(paymentConfig?.rentDueDay ?? 1);
  const [gracePeriodDays, setGracePeriodDays] = useState(paymentConfig?.gracePeriodDays ?? 0);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (paymentConfig) {
      setRentDueDay(paymentConfig.rentDueDay);
      setGracePeriodDays(paymentConfig.gracePeriodDays);
    }
  }, [paymentConfig]);

  useEffect(() => {
    const changed =
      rentDueDay !== (paymentConfig?.rentDueDay ?? 1) ||
      gracePeriodDays !== (paymentConfig?.gracePeriodDays ?? 0);
    setHasChanges(changed);
  }, [rentDueDay, gracePeriodDays, paymentConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard the in-flight window: handleSubmit awaits the PUT before hiding the button, so without
    // this a second click would fire a duplicate write. Only clear the unsaved-changes affordance
    // once the save actually persists.
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const ok = await onUpdate({ rentDueDay, gracePeriodDays });
      if (ok) setHasChanges(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="payment-terms-card">
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold m-0">Payment Terms</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-5">
          Configure when rent is due and the grace period
        </p>

        <div className="grid gap-4">
          {/* Rent Due Day */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="rent-due-day"
              className="text-sm font-medium text-muted-foreground"
            >
              Rent Due Day *
            </label>
            <select
              id="rent-due-day"
              data-testid="rent-due-day-input"
              value={rentDueDay}
              onChange={(e) => setRentDueDay(Number(e.target.value))}
              required
              aria-required="true"
              className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>
                  {day}{getDaySuffix(day)} of the month
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1 m-0">
              Day of the month when rent is due (1-28)
            </p>
          </div>

          {/* Grace Period */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="grace-period"
              className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1"
            >
              Grace Period (days)
              <HelpTooltip text="The number of days after the due date before a payment is considered late and late fees may apply. For example, if rent is due on the 1st with a 5-day grace period, late fees won't apply until the 6th. Many states require a minimum grace period — check your local landlord-tenant laws." />
            </label>
            <input
              id="grace-period"
              data-testid="grace-period-input"
              type="number"
              min="0"
              max="30"
              value={gracePeriodDays}
              onChange={(e) => setGracePeriodDays(Number(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
            />
            <p className="text-xs text-muted-foreground mt-1 m-0">
              Number of days after due date before late fees apply (0-30)
            </p>
          </div>
        </div>

        {(hasChanges || (paymentConfig != null && paymentConfig.termsSetAt == null)) && (
          <div className="flex justify-end mt-5">
            <button
              type="submit"
              data-testid="save-payment-settings-submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 min-h-[44px] disabled:opacity-60 disabled:cursor-default"
            >
              {isSubmitting ? 'Saving…' : 'Save Payment Terms'}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}

/* ── Card 3: Collection Setup ── */

interface CollectionSetupCardProps {
  paymentConfig: PaymentConfig | null;
  selectedMethod: MethodSelection;
  readiness: PaymentReadiness;
  fundingSources: LandlordFundingSource[];
  propertyBankInfo: PropertyBankInfo | null;
  linkedBankAccounts: LinkedBankAccount[];
  onUpdate: (data: UpdatePaymentConfigInput) => Promise<boolean>;
  onSetPropertyDefault: (fundingSourceId: string | null) => void;
  onSetMonitoringAccount: (linkedBankAccountId: string) => void;
}

export function CollectionSetupCard({
  paymentConfig,
  selectedMethod,
  readiness,
  fundingSources,
  propertyBankInfo,
  linkedBankAccounts,
  onUpdate,
  onSetPropertyDefault,
  onSetMonitoringAccount,
}: CollectionSetupCardProps) {
  const wantsAch = selectedMethod === 'ach';
  const wantsExternal = selectedMethod === 'external';

  const [destinationId, setDestinationId] = useState(paymentConfig?.destinationId ?? '');
  const [zelleRecipientInfo, setZelleRecipientInfo] = useState(paymentConfig?.zelleRecipientInfo ?? '');
  const [reminderEnabled, setReminderEnabled] = useState(paymentConfig?.reminderEnabled ?? false);
  const [reminderDaysBefore, setReminderDaysBefore] = useState(paymentConfig?.reminderDaysBefore ?? 3);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (paymentConfig) {
      setDestinationId(paymentConfig.destinationId ?? '');
      setZelleRecipientInfo(paymentConfig.zelleRecipientInfo ?? '');
      setReminderEnabled(paymentConfig.reminderEnabled ?? false);
      setReminderDaysBefore(paymentConfig.reminderDaysBefore ?? 3);
    }
  }, [paymentConfig]);

  useEffect(() => {
    const changed =
      destinationId !== (paymentConfig?.destinationId ?? '') ||
      zelleRecipientInfo !== (paymentConfig?.zelleRecipientInfo ?? '') ||
      reminderEnabled !== (paymentConfig?.reminderEnabled ?? false) ||
      reminderDaysBefore !== (paymentConfig?.reminderDaysBefore ?? 3);
    setHasChanges(changed);
  }, [destinationId, zelleRecipientInfo, reminderEnabled, reminderDaysBefore, paymentConfig]);

  const verifiedSources = fundingSources.filter(s => s.status === 'verified');
  const defaultDeposit = propertyBankInfo?.defaultDepositAccount ?? null;
  const monitoringAccount = propertyBankInfo?.monitoringAccount ?? null;
  const isUsingPropertyDefault = wantsAch && !destinationId && !!defaultDeposit;

  // Show sections based on the selected method intent
  const showAchSection = wantsAch;
  const showExternalSection = wantsExternal;

  if (!showAchSection && !showExternalSection) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: UpdatePaymentConfigInput = {};
    if (showAchSection) {
      data.destinationId = destinationId || null;
    }
    if (wantsExternal) {
      data.zelleRecipientInfo = zelleRecipientInfo || null;
      data.reminderEnabled = reminderEnabled;
      data.reminderDaysBefore = reminderDaysBefore;
    }
    // Guard the in-flight window (handleSubmit awaits the PUT before hiding the button); only clear
    // the unsaved-changes affordance once the save actually persists.
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const ok = await onUpdate(data);
      if (ok) setHasChanges(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold m-0">Collection Setup</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-5">
          Configure accounts and settings for collecting rent
        </p>

        <div className="flex flex-col gap-6">
          {/* ACH Section — shown when enabled or when bank accounts are available */}
          {showAchSection && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                {showExternalSection && (
                  <h4 className="text-sm font-semibold text-foreground m-0">ACH / Bank Transfer</h4>
                )}
                {readiness.ach.ready ? (
                  <span data-testid="ach-readiness-badge" className="text-xs font-medium text-success bg-success/10 border border-success/30 px-2 py-0.5 rounded">Ready</span>
                ) : (
                  <span data-testid="ach-readiness-badge" className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded">Setup required</span>
                )}
              </div>
              {!readiness.ach.ready && (
                <p className="text-xs text-warning mb-3 m-0">
                  {readiness.ach.issues.filter(i => i.severity === 'fatal').map(i => i.message).join('. ')}
                </p>
              )}
              <AchBankAccountSection
                destinationId={destinationId}
                defaultDeposit={defaultDeposit}
                isUsingPropertyDefault={isUsingPropertyDefault}
                verifiedSources={verifiedSources}
                propertyName={propertyBankInfo?.propertyName ?? ''}
                onDestinationChange={setDestinationId}
                onSetPropertyDefault={onSetPropertyDefault}
              />
            </div>
          )}

          {/* Divider when both sections shown */}
          {showAchSection && showExternalSection && (
            <div className="border-t border-border" />
          )}

          {/* External Section */}
          {showExternalSection && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                {showAchSection && (
                  <h4 className="text-sm font-semibold text-foreground m-0">External (Zelle / Cash)</h4>
                )}
                {readiness.external.ready ? (
                  <span data-testid="external-readiness-badge" className="text-xs font-medium text-success bg-success/10 border border-success/30 px-2 py-0.5 rounded">Ready</span>
                ) : (
                  <span data-testid="external-readiness-badge" className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded">Setup required</span>
                )}
              </div>
              {!readiness.external.ready && (
                <p className="text-xs text-warning mb-3 m-0">
                  {readiness.external.issues.filter(i => i.severity === 'fatal').map(i => i.message).join('. ')}
                </p>
              )}
              <div className="flex flex-col gap-4">
                <ExternalBankAccountSection
                  monitoringAccount={monitoringAccount}
                  linkedBankAccounts={linkedBankAccounts}
                  propertyName={propertyBankInfo?.propertyName ?? ''}
                  onSetMonitoringAccount={onSetMonitoringAccount}
                />

                {/* Mode status — read-only, system-managed */}
                {paymentConfig && paymentConfig.paymentMethod === 'external' && paymentConfig.externalPhase && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium inline-flex items-center gap-1">
                      Detection Mode
                      <HelpTooltip text="Learning: The system is learning your tenants' payment patterns. Confirmed matches record payments, but auto-matches are held until the system proves reliable. No late fees are assessed during this phase.\n\nActive: The system has proven reliable and now auto-applies matched payments to the ledger. Late fees can be assessed (with your approval).\n\nPromotion from Learning to Active happens automatically once a full billing cycle passes and the balance reaches zero via external payments." />
                    </label>
                    <div className="flex items-center gap-2">
                      {paymentConfig.externalPhase === 'learning' ? (
                        <span
                          className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded"
                          data-testid="external-mode-badge"
                        >
                          Learning
                        </span>
                      ) : (
                        <span
                          className="text-xs font-medium text-success bg-success/10 border border-success/30 px-2 py-0.5 rounded"
                          data-testid="external-mode-badge"
                        >
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground m-0">
                      {paymentConfig.externalPhase === 'learning'
                        ? 'The system is learning your tenants\u2019 payment patterns. Confirmed matches will record payments; auto-matches are held until promotion.'
                        : 'All matched payments are automatically recorded to the ledger.'}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-4" data-testid="external-payment-settings-section">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="zelle-recipient-info"
                      className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1"
                    >
                      Zelle Recipient Info
                      <HelpTooltip text="Your Zelle email or phone number. This is included in payment reminder emails sent to tenants so they know where to send their payment." />
                    </label>
                    <input
                      id="zelle-recipient-info"
                      data-testid="zelle-recipient-info-input"
                      type="text"
                      value={zelleRecipientInfo}
                      onChange={(e) => setZelleRecipientInfo(e.target.value)}
                      placeholder="Email or phone number"
                      className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
                    />
                    <p className="text-xs text-muted-foreground mt-1 m-0">
                      Zelle email or phone shown to tenants for payment instructions
                    </p>
                  </div>


                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                      <input
                        id="reminder-enabled"
                        data-testid="reminder-enabled-input"
                        type="checkbox"
                        checked={reminderEnabled}
                        onChange={(e) => setReminderEnabled(e.target.checked)}
                        className="h-4 w-4 text-primary border-input rounded"
                      />
                      <label htmlFor="reminder-enabled" className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1">
                        Enable Payment Reminders
                        <HelpTooltip text="When enabled, tenants receive an email reminder before their rent is due. The reminder includes your Zelle info (if provided) and payment instructions. Useful for external payment leases where tenants need to initiate payment themselves." />
                      </label>
                    </div>

                    {reminderEnabled && (
                      <div className="ml-7 flex flex-col gap-1.5">
                        <label
                          htmlFor="reminder-days-before"
                          className="text-sm font-medium text-muted-foreground"
                        >
                          Days Before Due Date
                        </label>
                        <input
                          id="reminder-days-before"
                          data-testid="reminder-days-before-input"
                          type="number"
                          min="1"
                          value={reminderDaysBefore}
                          onChange={(e) => setReminderDaysBefore(Number(e.target.value))}
                          className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Unlike PaymentTermsCard / LateFeeConfigSection, there is NO first-config affordance here:
            those cards show Save when their Decision-C marker (termsSetAt / lateFeesSetAt) is null so a
            landlord can confirm default values and stamp the marker. Collection Setup has no such
            marker — the deposit account / Zelle fields ARE the persisted state, so a no-change Save
            would be a no-op. The button therefore appears only when there is something to save. */}
        {hasChanges && (
          <div className="flex justify-end mt-5">
            <button
              type="submit"
              data-testid="save-collection-setup-submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 min-h-[44px] disabled:opacity-60 disabled:cursor-default"
            >
              {isSubmitting ? 'Saving…' : 'Save Collection Setup'}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}

/* ── ACH Bank Account Section ── */

interface AchBankAccountSectionProps {
  destinationId: string;
  defaultDeposit: PropertyBankInfo['defaultDepositAccount'];
  isUsingPropertyDefault: boolean;
  verifiedSources: LandlordFundingSource[];
  propertyName: string;
  onDestinationChange: (id: string) => void;
  onSetPropertyDefault: (fundingSourceId: string | null) => void;
}

function AchBankAccountSection({
  destinationId,
  defaultDeposit,
  isUsingPropertyDefault,
  verifiedSources,
  propertyName,
  onDestinationChange,
  onSetPropertyDefault,
}: AchBankAccountSectionProps) {
  return (
    <div data-testid="ach-deposit-account-section" className="flex flex-col gap-3">
      <p
        className="text-xs text-muted-foreground bg-accent/30 border border-border/60 rounded-md px-3 py-2 m-0"
        data-testid="ach-fee-disclaimer"
      >
        Stripe charges a {ACH_FEE} fee per ACH transaction, which is deducted from the deposit to your bank account.
      </p>

      {/* Property default info */}
      {defaultDeposit && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-md text-sm">
          <span className="text-primary">
            Property default ({propertyName}): {formatAccountLabel(defaultDeposit)}
          </span>
          {isUsingPropertyDefault && (
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
              Active
            </span>
          )}
        </div>
      )}

      {!defaultDeposit && verifiedSources.length > 0 && (
        <p className="text-xs text-muted-foreground italic m-0">
          No property default set. Select a deposit account below.
        </p>
      )}

      {/* Deposit account selector */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="destination-account"
          className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1"
        >
          Deposit Account {!defaultDeposit ? '*' : ''}
          <HelpTooltip text="The verified bank account where ACH rent payments will be deposited. You can set a default for all leases on this property, or override it per lease. Only verified bank accounts appear here — add or verify accounts in Settings." />
        </label>
        <select
          id="destination-account"
          data-testid="destination-account-input"
          value={destinationId}
          onChange={(e) => onDestinationChange(e.target.value)}
          required={!defaultDeposit}
          aria-required={!defaultDeposit}
          className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
        >
          {defaultDeposit ? (
            <option value="">Use property default ({formatAccountLabel(defaultDeposit)})</option>
          ) : (
            <option value="">Select a bank account</option>
          )}
          {verifiedSources.map(source => {
            const label = formatSourceLabel(source);
            return (
              <option key={source.id} value={source.id}>
                {label}
              </option>
            );
          })}
        </select>
        <p className="text-xs text-muted-foreground mt-1 m-0">
          {defaultDeposit
            ? 'Leave as property default or select a specific account for this lease'
            : 'Bank account where rent payments will be deposited'}
        </p>
      </div>

      {/* Set as property default button */}
      {destinationId && destinationId !== defaultDeposit?.id && (
        <button
          type="button"
          data-testid="set-property-default-button"
          onClick={() => onSetPropertyDefault(destinationId)}
          className="self-start px-3 py-1.5 text-xs font-medium text-primary border border-primary/20 rounded hover:bg-primary/5 min-h-[36px]"
        >
          Set as default for all leases on {propertyName}
        </button>
      )}

      {verifiedSources.length === 0 && (
        <p className="text-sm text-warning mt-2 m-0">
          No verified bank accounts available. Please add a bank account in the{' '}
          <Link to="/bank-accounts" className="underline">Bank Accounts</Link> section.
        </p>
      )}
    </div>
  );
}

/* ── External Bank Account Section ── */

interface ExternalBankAccountSectionProps {
  monitoringAccount: PropertyBankInfo['monitoringAccount'];
  linkedBankAccounts: LinkedBankAccount[];
  propertyName: string;
  onSetMonitoringAccount: (linkedBankAccountId: string) => void;
}

function ExternalBankAccountSection({ monitoringAccount, linkedBankAccounts, propertyName, onSetMonitoringAccount }: ExternalBankAccountSectionProps) {
  const activeAccounts = linkedBankAccounts.filter(a => a.status === 'active');

  return (
    <div data-testid="external-monitoring-account-section" className="flex flex-col gap-2">
      {monitoringAccount ? (
        <div className="flex items-center justify-between px-3 py-3 bg-success/10 border border-success/20 rounded-md">
          <div>
            <p className="text-sm font-medium text-success m-0">
              Monitoring: {monitoringAccount.institutionName} ****{monitoringAccount.accountMask}
            </p>
            <p className="text-xs text-success mt-0.5 m-0">
              {monitoringAccount.accountName} &middot; Linked to {propertyName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
              monitoringAccount.status === 'active'
                ? 'bg-success/10 text-success'
                : 'bg-warning/10 text-warning'
            }`}>
              {monitoringAccount.status}
            </span>
            <Link
              to="/bank-accounts"
              className="text-xs text-success underline hover:text-success"
            >
              Manage
            </Link>
          </div>
        </div>
      ) : activeAccounts.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="monitoring-account"
            className="text-sm font-medium text-muted-foreground"
          >
            Monitoring Account *
          </label>
          <select
            id="monitoring-account"
            data-testid="monitoring-account-select"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) onSetMonitoringAccount(e.target.value);
            }}
            className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
          >
            <option value="">Select a linked bank account</option>
            {activeAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.institutionName} — {account.accountName} ****{account.accountMask}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1 m-0">
            Select a linked bank account to monitor for incoming payments on {propertyName}
          </p>
        </div>
      ) : (
        <div className="px-3 py-3 bg-accent/50 border border-border rounded-md">
          <p className="text-sm text-muted-foreground m-0">
            No linked bank accounts available for transaction monitoring.
          </p>
          <Link
            to="/bank-accounts"
            className="text-sm text-primary underline hover:text-primary"
          >
            Link a bank account
          </Link>
        </div>
      )}
      <p className="text-xs text-muted-foreground m-0">
        The monitoring account is set at the property level. All leases on this property share the same monitored account.
      </p>
    </div>
  );
}

/* ── Helpers ── */

function formatAccountLabel(account: { bankName: string; accountMask: string; friendlyName: string | null; isPrimary: boolean }) {
  const details = `${account.bankName} ****${account.accountMask}`;
  const label = account.friendlyName ? `${account.friendlyName} (${details})` : details;
  return account.isPrimary ? `${label} (Primary)` : label;
}

function formatSourceLabel(source: LandlordFundingSource) {
  const details = `${source.bankName} ****${source.accountMask}`;
  const label = source.friendlyName ? `${source.friendlyName} (${details})` : details;
  return source.isPrimary ? `${label} (Primary)` : label;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
